/**
 * templateHelpers.js — Shared normalization + section rendering logic
 * for all resume templates. Ensures every template has consistent
 * data handling without duplicating code.
 */

/**
 * normalizePoints — canonicalize bullet arrays across all known key aliases.
 *
 * Handles BUG-006/007: different parts of the system use:
 *   experience bullets: "points" | "bullets" | "responsibilities"
 *   project bullets:    "points" | "bullets" | "description"
 *
 * Always returns a flat string array.
 */
export function normalizePoints(item) {
    if (!item) return [];
    const raw = item.points ?? item.bullets ?? item.responsibilities ?? item.description ?? [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === 'string' && raw.trim()) return [raw];
    return [];
}

/** Normalize resume data — safe arrays + link parsing */
export function normalizeTemplateData(resumeData) {
    const {
        name, email, phone, city, role, summary,
        skills, skillCategories, experience, education,
        certifications, languages, links, achievements, extracurricular,
        projects, internships, photo_url, interests, hobbies,
        awards, honors, publications, volunteer, volunteering, references,
    } = resumeData;


    const educationList  = (Array.isArray(education) ? education : (education ? [education] : []))
      .map(edu => ({
        ...edu,
        degree: edu.degree || edu.degreeName || edu.course || '',
        college: edu.college || edu.school || edu.university || edu.institution || ''
      }));
    const languageList   = Array.isArray(languages)       ? languages       : [];
    const certList       = Array.isArray(certifications)  ? certifications  : [];
    const achieveList    = Array.isArray(achievements)    ? achievements    : [];
    const extraList      = Array.isArray(extracurricular) ? extracurricular : [];
    const summaryText    = (summary && summary.trim())    || "";

    // Normalize experience & project bullet points using all known aliases
    const normalizedExperience = Array.isArray(experience)
        ? experience.map(exp => {
            const pts = normalizePoints(exp);
            return { ...exp, points: pts, bullets: pts };
          })
        : [];
    const normalizedProjects = Array.isArray(projects)
        ? projects.map(proj => {
            const pts = normalizePoints(proj);
            return { ...proj, points: pts, bullets: pts };
          })
        : [];
    const normalizedInternships = Array.isArray(internships)
        ? internships.map(i => {
            const pts = normalizePoints(i);
            return { ...i, points: pts, bullets: pts };
          })
        : [];

    const contactItems = [];
    if (city)  contactItems.push(city);
    if (email) contactItems.push(email);
    if (phone) contactItems.push(phone);

    const stripUrl = (url) => url ? url.replace(/^https?:\/\/(www\.)?/, '') : '';
    const linkItems = [];
    if (links?.linkedin)  linkItems.push({ href: links.linkedin,  label: stripUrl(links.linkedin),  key: 'linkedin' });
    if (links?.github)    linkItems.push({ href: links.github,    label: stripUrl(links.github),    key: 'github' });
    if (links?.portfolio) linkItems.push({ href: links.portfolio, label: stripUrl(links.portfolio), key: 'portfolio' });


    // Normalise skillCategories — if present use them, otherwise leave null so
    // templates can fall back to auto-categorisation from the flat skills array.
    const normalizedSkillCategories = Array.isArray(skillCategories) && skillCategories.length > 0
        ? skillCategories.map((cat, i) => ({
            id: cat.id || `cat_${i}`,
            name: cat.name || 'Skills',
            skills: Array.isArray(cat.skills) ? cat.skills : [],
          }))
        : null;

    return {
        name, email, phone, city, role, summaryText,
        skills:             Array.isArray(skills)      ? skills      : [],
        skillCategories:    normalizedSkillCategories,
        experience:         normalizedExperience,
        projects:     normalizedProjects,
        internships:  normalizedInternships,
        educationList, languageList, certList, achieveList, extraList,
        contactItems, linkItems,
        links:   links   || {},
        photoUrl: photo_url || null,
        interests: Array.isArray(interests) ? interests : (interests ? [interests] : []),
        hobbies:   Array.isArray(hobbies)   ? hobbies   : (hobbies   ? [hobbies]   : []),
        // New sections for UniversalSectionRenderer
        awards:       Array.isArray(awards)        ? awards        : (Array.isArray(honors)    ? honors    : []),
        publications: Array.isArray(publications)  ? publications  : [],
        volunteer:    Array.isArray(volunteer)      ? volunteer     : (Array.isArray(volunteering) ? volunteering : []),
        references:   Array.isArray(references)    ? references    : [],
    };

}

/** Safely extract text from a point (string or object) */
export function safePoint(point) {
    if (typeof point === "string") return point;
    if (point?.description) return point.description;
    if (point?.achievements) return point.achievements;
    return JSON.stringify(point);
}

/**
 * makeHref — ensures a URL has a protocol prefix so anchor tags work.
 * "linkedin.com/in/john" → "https://linkedin.com/in/john"
 */
export function makeHref(url) {
    if (!url) return '#';
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
}

/**
 * ResumeLink — renders a clean, clickable anchor tag for resume links.
 * - label: display text (stripped URL, e.g. "linkedin.com/in/john")
 * - href:  full URL for the href attribute
 * - style: optional inline styles
 *
 * Opens in a new tab with noopener for security.
 * Shows a tiny ↗ icon on hover to hint it's clickable.
 */
export function ResumeLink({ href, label, style = {}, className = '' }) {
    const fullHref = makeHref(href || label);
    return (
        <a
            href={fullHref}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
            style={{
                textDecoration: 'none',
                cursor: 'pointer',
                borderBottom: '1px dotted currentColor',
                ...style,
            }}
            onClick={(e) => e.stopPropagation()} // don't trigger canvas pan
        >
            {label}
        </a>
    );
}


/**
 * categorizeSkills — auto-groups a flat skills array into categories.
 *
 * Returns an array like:
 *   [{ label: "Technical", items: ["Python","React.js",...] }, ...]
 *
 * If no clear categories emerge (< 4 skills total), returns a single
 * "Skills" bucket so templates always get a consistent structure.
 */

const TECHNICAL_KEYWORDS = new Set([
    // Languages
    'python','javascript','typescript','java','c++','c#','c','kotlin','swift',
    'go','golang','ruby','php','r','matlab','scala','rust','dart','bash','shell',
    // Web frameworks
    'react','react.js','reactjs','vue','vue.js','angular','next.js','nextjs',
    'node','node.js','nodejs','express','express.js','django','flask','fastapi',
    'spring','laravel','rails','asp.net',
    // Databases
    'sql','mysql','postgresql','mongodb','redis','sqlite','oracle','firebase',
    'dynamodb','cassandra','elasticsearch','nosql',
    // Mobile
    'flutter','react native','android','ios','swift','kotlin',
    // Data / ML
    'tensorflow','pytorch','scikit-learn','pandas','numpy','machine learning',
    'deep learning','nlp','computer vision','data science','ml','ai',
    // Cloud
    'aws','azure','gcp','google cloud','heroku','vercel','netlify','digitalocean',
    // DevOps
    'docker','kubernetes','ci/cd','jenkins','github actions','terraform',
    'ansible','linux','unix','nginx','apache',
    // APIs
    'rest','rest api','rest apis','graphql','soap','api','postman',
    // Version control
    'git','github','gitlab','bitbucket',
    // Data engineering
    'spark','hadoop','kafka','airflow','etl',
]);

const TOOLS_KEYWORDS = new Set([
    // HR tools
    'hrms','hris','zoho','darwinbox','greythr','bamboohr','workday','sap hr',
    'keka','sumhr','freshteam','peoplesoft',
    // Office tools
    'microsoft excel','excel','microsoft word','word','microsoft powerpoint',
    'powerpoint','google sheets','google docs','google workspace','ms office',
    'microsoft office','libreoffice','outlook','google forms',
    // Design tools
    'figma','adobe photoshop','photoshop','illustrator','adobe illustrator',
    'canva','sketch','invision','zeplin','xd','adobe xd','procreate',
    // Project management
    'jira','trello','asana','notion','confluence','monday.com','basecamp',
    'clickup','linear','slack','teams','microsoft teams','zoom',
    // Analytics tools
    'google analytics','tableau','power bi','looker','mixpanel','hotjar',
    'semrush','ahrefs','hubspot','salesforce','zoho crm',
    // Finance tools
    'tally','quickbooks','sap','sap fico','sap mm','oracle financials','busy',
    // Dev tools
    'vs code','visual studio','eclipse','intellij','pycharm','xcode',
    'android studio','postman','insomnia','swagger',
]);

const SOFT_KEYWORDS = new Set([
    'communication','leadership','teamwork','problem solving','critical thinking',
    'time management','adaptability','creativity','collaboration','attention to detail',
    'interpersonal','presentation','negotiation','emotional intelligence',
    'conflict resolution','cross-functional collaboration','stakeholder management',
    'process optimization','analytical thinking','decision making',
    'customer service','client management','relationship management',
    'multitasking','organizational','planning','strategic thinking',
    'documentation','report writing','training','mentoring','coaching',
]);

/** Strings that look like skill sub-category headers — strip these before categorizing */
const SKILL_HEADER_LABELS = new Set([
    'technical skills', 'soft skills', 'other skills', 'core competencies',
    'key skills', 'professional skills', 'tools & technologies', 'tools & software',
    'tools & tech', 'languages known', 'interpersonal skills', 'domain skills',
    'hard skills', 'analytical skills', 'management skills', 'computer skills',
]);

export function categorizeSkills(skills) {
    if (!skills || skills.length === 0) return [];

    // Strip any sub-category header strings the AI may have accidentally included
    const cleanedSkills = skills.filter(s => {
        if (typeof s !== 'string') return false;
        const stripped = s.toLowerCase().trim().replace(/:$/, '');
        return !SKILL_HEADER_LABELS.has(stripped);
    });

    if (cleanedSkills.length === 0) return [];

    const technical = [];
    const tools     = [];
    const soft      = [];
    const other     = [];

    cleanedSkills.forEach(skill => {
        const lower = skill.toLowerCase().trim();

        if (TECHNICAL_KEYWORDS.has(lower)) {
            technical.push(skill);
        } else if (TOOLS_KEYWORDS.has(lower)) {
            tools.push(skill);
        } else if (SOFT_KEYWORDS.has(lower)) {
            soft.push(skill);
        } else {
            // Heuristic: if it contains common tech indicators, treat as technical
            if (/\.(js|ts|py|rb|go|net|css|html)$/i.test(lower) ||
                /api|sdk|framework|database|cloud|server/i.test(lower)) {
                technical.push(skill);
            } else {
                other.push(skill);
            }
        }
    });

    const categories = [];

    if (technical.length > 0) {
        categories.push({ label: 'Technical Skills', items: technical });
    }
    if (tools.length > 0) {
        categories.push({ label: 'Tools & Software', items: tools });
    }
    if (soft.length > 0) {
        categories.push({ label: 'Professional Skills', items: soft });
    }
    if (other.length > 0) {
        if (categories.length > 0) {
            // Absorb into nearest existing category
            categories[0].items.push(...other);
        } else {
            // ALL skills are domain/HR/non-tech — show as single Skills block
            categories.push({ label: 'Skills', items: other });
        }
    }

    // Collapse to one bucket if only 1 skill category ended up populated
    if (categories.length === 1) return categories;

    // Filter out empty categories
    return categories.filter(c => c.items.length > 0);
}

/**
 * sortSkills — returns a flat ordered skills array.
 * Order: technical → tools → soft → other.
 * Use this for clean grid displays where you want logical ordering
 * but NO visible category labels (avoids the cluttered look).
 */
export function sortSkills(skills) {
    if (!skills || skills.length === 0) return [];
    const cats = categorizeSkills(skills);
    // Flatten in order, dedup
    const seen = new Set();
    const result = [];
    cats.forEach(cat => cat.items.forEach(s => {
        if (!seen.has(s)) { seen.add(s); result.push(s); }
    }));
    return result;
}


/**
 * detectRoleCategory — maps a job title/role string to one of 5 role categories.
 * Returns: 'tech' | 'mba' | 'marketing' | 'finance' | 'design' | 'general'
 */
export function detectRoleCategory(role) {
    if (!role) return 'general';
    const r = role.toLowerCase();

    if (/software|developer|engineer|programmer|devops|data\s*scien|machine\s*learn|\bml\b|\bai\b|backend|frontend|full.?stack|\bcloud\b|\bsre\b|site\s*reliab|\bqa\b|tester|\bweb\s*dev|android|\bios\b|mobile\s*dev|cyber|network\s*eng|blockchain|embedded|firmware|system\s*admin|data\s*eng|data\s*analy/.test(r)) return 'tech';

    if (/\bmba\b|business\s*(analyst|dev|manager|strat|intel)|management\s*(consult|trainee)|general\s*manager|\bgm\b|\bceo\b|\bcfo\b|\bcoo\b|\bvp\b|vice\s*pres|director|operations\s*manag|product\s*manag|project\s*manag|programme\s*manag|strategy|strateg|consult|\bpgdm\b|\bbba\b/.test(r)) return 'mba';

    if (/market|sales\s*(exec|manag|rep|lead)|brand|growth\s*hack|\bseo\b|\bsem\b|content\s*(market|writ|creat)|social\s*media|digital\s*market|\bpr\b|public\s*relat|advertis|campaign|\bcrm\b|account\s*manag|business\s*dev|\bb2b\b|\bb2c\b|ecommerce|product\s*market/.test(r)) return 'marketing';

    if (/financ|account|\bca\b|chartered\s*account|\bcfa\b|\bcpa\b|\bcma\b|audit|\btax\b|banking|investment\s*bank|equity\s*res|credit\s*anal|risk\s*anal|treasury|actuari|\bbfsi\b|wealth\s*manag|fund\s*manag|financial\s*anal/.test(r)) return 'finance';

    if (/design|designer|\bux\b|\bui\b|user\s*exper|user\s*inter|graphic|creative\s*dir|art\s*dir|visual\s*design|product\s*design|motion\s*design|animat|illustrat|figma|adobe|brand\s*design/.test(r)) return 'design';

    return 'general';
}

/** Map role category to recommended template ID */
export const CATEGORY_TEMPLATE_MAP = {
    tech:      'techpro',
    mba:       'execpro',
    marketing: 'brandpro',
    finance:   'finpro',
    design:    'creativepro',
    general:   'classic',
};

/**
 * categorizeSkillsByRole — like categorizeSkills() but uses role-appropriate group labels.
 * Returns [{ label, items }] with headings suited to the role category.
 */
export function categorizeSkillsByRole(skills, category = 'general') {
    if (!skills || skills.length === 0) return [];

    if (category === 'tech') {
        // Reuse existing TechPro logic
        const langs      = skills.filter(s => /\b(java|python|javascript|typescript|php|c\+\+|c#|kotlin|swift|golang|ruby|sql|html|css|bash|shell|scala|perl|dart|r\b)\b/i.test(s));
        const frameworks = skills.filter(s => /spring|react|next\.js|angular|vue|django|flask|fastapi|node|express|bootstrap|laravel|rails|asp\.net/i.test(s));
        const databases  = skills.filter(s => /mysql|mongodb|postgres|sqlite|redis|oracle|firebase|dynamodb|nosql|cassandra|rest\s*api|graphql|api/i.test(s));
        const infra      = skills.filter(s => /linux|nginx|docker|kubernetes|aws|azure|gcp|git\b|github|ci\/cd|jenkins|terraform|ansible|cloud/i.test(s));
        const assigned   = new Set([...langs, ...frameworks, ...databases, ...infra].map(s => s.toLowerCase()));
        const other      = skills.filter(s => !assigned.has(s.toLowerCase()));
        const cats = [];
        if (langs.length)      cats.push({ label: 'Languages', items: langs });
        if (frameworks.length) cats.push({ label: 'Frameworks & Libraries', items: frameworks });
        if (databases.length)  cats.push({ label: 'Databases & APIs', items: databases });
        if (infra.length)      cats.push({ label: 'Cloud & DevOps', items: infra });
        if (other.length)      cats.push({ label: 'Tools & Practices', items: other });
        return cats.length ? cats : [{ label: 'Technical Skills', items: skills }];
    }

    if (category === 'mba') {
        const STRATEGIC  = new Set(['strategy', 'business development', 'strategic planning', 'market analysis', 'competitive analysis', 'financial modeling', 'p&l management', 'revenue growth', 'go-to-market', 'change management', 'stakeholder management', 'board reporting', 'business analytics']);
        const OPERATIONS = new Set(['project management', 'operations management', 'supply chain', 'process improvement', 'lean', 'six sigma', 'agile', 'scrum', 'quality management', 'risk management', 'vendor management', 'budgeting', 'forecasting', 'kpi', 'performance management', 'team leadership']);
        const TOOLS      = new Set(['excel', 'microsoft excel', 'powerpoint', 'ms office', 'google workspace', 'tableau', 'power bi', 'salesforce', 'jira', 'sap', 'oracle', 'hubspot', 'notion', 'sql', 'zoho', 'monday.com']);
        const strategic = [], operational = [], tools = [], soft = [];
        skills.forEach(s => {
            const l = s.toLowerCase().trim();
            if (TOOLS.has(l)) tools.push(s);
            else if (STRATEGIC.has(l)) strategic.push(s);
            else if (OPERATIONS.has(l)) operational.push(s);
            else soft.push(s);
        });
        const cats = [];
        if (strategic.length)   cats.push({ label: 'Strategic & Leadership', items: strategic });
        if (operational.length) cats.push({ label: 'Operations & Process', items: operational });
        if (tools.length)       cats.push({ label: 'Tools & Technology', items: tools });
        if (soft.length)        cats.push({ label: 'Professional Skills', items: soft });
        return cats.length ? cats : [{ label: 'Core Competencies', items: skills }];
    }

    if (category === 'marketing') {
        const DIGITAL   = new Set(['seo', 'sem', 'ppc', 'google ads', 'facebook ads', 'meta ads', 'email marketing', 'affiliate marketing', 'performance marketing', 'growth hacking', 'conversion optimization', 'cro', 'lead generation']);
        const ANALYTICS = new Set(['google analytics', 'ga4', 'mixpanel', 'semrush', 'ahrefs', 'tableau', 'power bi', 'looker', 'hotjar', 'hubspot', 'salesforce', 'crm', 'sql']);
        const CONTENT   = new Set(['content marketing', 'copywriting', 'content writing', 'social media', 'instagram', 'linkedin', 'youtube', 'video marketing', 'podcast', 'blogging', 'pr', 'brand management', 'storytelling']);
        const TOOLS     = new Set(['canva', 'adobe photoshop', 'illustrator', 'figma', 'mailchimp', 'klaviyo', 'hootsuite', 'buffer', 'wordpress', 'shopify', 'woocommerce', 'webflow']);
        const digital = [], analytics = [], content = [], tools = [], other = [];
        skills.forEach(s => {
            const l = s.toLowerCase().trim();
            if (DIGITAL.has(l)) digital.push(s);
            else if (ANALYTICS.has(l)) analytics.push(s);
            else if (CONTENT.has(l)) content.push(s);
            else if (TOOLS.has(l)) tools.push(s);
            else other.push(s);
        });
        const cats = [];
        if (digital.length)   cats.push({ label: 'Digital Marketing', items: digital });
        if (analytics.length) cats.push({ label: 'Analytics & SEO', items: analytics });
        if (content.length)   cats.push({ label: 'Content & Social', items: content });
        if (tools.length)     cats.push({ label: 'Tools & Platforms', items: tools });
        if (other.length)     cats.push({ label: 'Skills', items: other });
        return cats.length ? cats : [{ label: 'Marketing Skills', items: skills }];
    }

    if (category === 'finance') {
        const ANALYSIS  = new Set(['financial analysis', 'financial modeling', 'valuation', 'equity research', 'credit analysis', 'risk analysis', 'portfolio management', 'investment analysis', 'dcf', 'ratio analysis', 'forecasting', 'budgeting', 'cash flow', 'p&l']);
        const TOOLS     = new Set(['tally', 'quickbooks', 'sap', 'sap fico', 'oracle financials', 'excel', 'microsoft excel', 'power bi', 'tableau', 'bloomberg', 'ms office']);
        const COMPLIANCE = new Set(['ifrs', 'ind as', 'gaap', 'tax compliance', 'gst', 'income tax', 'direct tax', 'indirect tax', 'audit', 'statutory audit', 'internal audit', 'regulatory compliance', 'rbi guidelines', 'sebi', 'companies act']);
        const financial = [], tools = [], compliance = [], soft = [];
        skills.forEach(s => {
            const l = s.toLowerCase().trim();
            if (TOOLS.has(l)) tools.push(s);
            else if (ANALYSIS.has(l)) financial.push(s);
            else if (COMPLIANCE.has(l)) compliance.push(s);
            else soft.push(s);
        });
        const cats = [];
        if (financial.length)  cats.push({ label: 'Financial Analysis', items: financial });
        if (tools.length)      cats.push({ label: 'Tools & Software', items: tools });
        if (compliance.length) cats.push({ label: 'Compliance & Regulation', items: compliance });
        if (soft.length)       cats.push({ label: 'Professional Skills', items: soft });
        return cats.length ? cats : [{ label: 'Core Competencies', items: skills }];
    }

    if (category === 'design') {
        const TOOLS      = new Set(['figma', 'sketch', 'adobe xd', 'adobe photoshop', 'photoshop', 'illustrator', 'adobe illustrator', 'invision', 'zeplin', 'canva', 'procreate', 'blender', 'cinema 4d', 'after effects', 'premiere pro']);
        const PROTO      = new Set(['prototyping', 'wireframing', 'user testing', 'usability testing', 'a/b testing', 'interaction design', 'motion design', 'animation', 'framer', 'principle', 'marvel']);
        const RESEARCH   = new Set(['user research', 'ux research', 'design thinking', 'heuristic evaluation', 'persona creation', 'journey mapping', 'information architecture', 'card sorting', 'contextual inquiry']);
        const VISUAL     = new Set(['typography', 'color theory', 'layout', 'grid systems', 'brand identity', 'visual design', 'graphic design', 'logo design', 'illustration', 'icon design']);
        const dTools = [], proto = [], research = [], visual = [], other = [];
        skills.forEach(s => {
            const l = s.toLowerCase().trim();
            if (TOOLS.has(l)) dTools.push(s);
            else if (PROTO.has(l)) proto.push(s);
            else if (RESEARCH.has(l)) research.push(s);
            else if (VISUAL.has(l)) visual.push(s);
            else other.push(s);
        });
        const cats = [];
        if (dTools.length)   cats.push({ label: 'Design Tools', items: dTools });
        if (proto.length)    cats.push({ label: 'Prototyping & Testing', items: proto });
        if (research.length) cats.push({ label: 'Research Methods', items: research });
        if (visual.length)   cats.push({ label: 'Visual Communication', items: visual });
        if (other.length)    cats.push({ label: 'Skills', items: other });
        return cats.length ? cats : [{ label: 'Design Skills', items: skills }];
    }

    // General fallback — use existing categorizeSkills
    return categorizeSkills(skills);
}

